<template>
  <LayoutLogin>
    <template v-slot:content>
      <LayoutLoginCard>
        <div class="d-flex justify-center my-6">
          <Logo type="big" />
        </div>

        <p class="my-4 text-body-1 text-center">
          {{ $t('auth.access.validating_credentials') }}
        </p>

        <LoaderSpinner v-if="isLoading" />
      </LayoutLoginCard>
    </template>
  </LayoutLogin>
</template>

<script>
import { mapActions } from 'vuex'
import * as Sentry from '@sentry/browser'

import { isOfficeEnvironment, sendDataToOffice } from '@/lib/office'
import mixin from '@/mixins/mixin.vue'
import authMixin from '@/mixins/authMixin.vue'

import LayoutLogin from '@/components/Core/Layout/LayoutLogin'
import LayoutLoginCard from './components/LayoutLoginCard.vue'
import Logo from '@/components/Core/Logo/Logo.vue'
import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'

export default {
  name: 'MicrosoftRedirectView',

  components: {
    LayoutLogin,
    LayoutLoginCard,
    Logo,
    LoaderSpinner,
  },

  mixins: [mixin, authMixin],

  data() {
    return {
      isLoading: true,
    }
  },

  async mounted() {
    if (this.$route.query?.accessToken) return this.signIn()
    if (this.$route.query?.error) return this.handleError()
  },

  watch: {
    // Query params take some time to be available, so we have to watch for changes and react accordingly
    '$route.query': {
      handler() {
        if (this.$route.query?.accessToken) return this.signIn()
        if (this.$route.query?.error) return this.handleError()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions({
      loginWithAzureToken: 'loginWithAzureToken',
    }),

    async signIn() {
      try {
        const reverSession = await this.loginWithAzureToken({
          accessToken: this.$route.query?.accessToken,
          expiresOn: this.$route.query?.expiresOn,
        })
        const callback = sessionStorage.getItem('callback')
        const redirect = sessionStorage.getItem('redirect')

        this.$track('session.Start', this.$store, {
          process: 'ActiveDirectoryLogin',
          status: 'success',
        })

        if (isOfficeEnvironment()) {
          return sendDataToOffice({
            account: reverSession.account,
            accessToken: reverSession.accessToken,
          })
        }

        if (callback)
          return this.navigateToExternalRoute(
            `${callback}?accessToken=${reverSession.accessToken}`
          )

        this.setLoginData(reverSession)
        this.replaceRoute(redirect ?? '/home')
      } catch (err) {
        this.$track('session.Start', this.$store, {
          process: 'ActiveDirectoryLogin',
          status: 'fail',
        })

        Sentry.captureException(`Microsoft login error: ${err}}`)

        this.showError(
          `${this.$t('error.auth.login_failed_please_try_again_later')} - 
          ${err?.response?.data?.message ?? err}`
        )

        this.replaceRoute({ name: 'access' })
      }
    },

    handleError() {
      this.showError(this.$route.query?.error)
      this.replaceRoute({ name: 'access' })
    },
  },
}
</script>
